/**
 * Admin API service. Talks to the same FastAPI backend as the shell.
 */

const BASE = '/api/v1';

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Cuerpo de una petición que PUEDE llevar adjuntos (paso 34).
 *
 * Sin ficheros, el JSON de siempre. Con ficheros, `multipart/form-data`: el
 * mismo JSON en el campo `datos` y cada fichero en `adjuntos`. Va todo en UNA
 * petición a propósito: el backend guarda mensaje (o alta), adjuntos y tiempo
 * en un solo commit, o nada. Sin `Content-Type` a mano: con `FormData` lo pone
 * el navegador, con su `boundary`.
 */
function cuerpoConAdjuntos(
  token: string,
  datos: unknown,
  adjuntos: File[] = [],
): { headers: Record<string, string>; body: string | FormData } {
  if (adjuntos.length === 0) {
    return { headers: authHeaders(token), body: JSON.stringify(datos) };
  }
  const form = new FormData();
  form.append('datos', JSON.stringify(datos));
  for (const f of adjuntos) form.append('adjuntos', f, f.name);
  return { headers: { Authorization: `Bearer ${token}` }, body: form };
}

async function handleResponse<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    const msg = body?.detail?.message ?? body?.detail ?? `Error ${resp.status}`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────

export interface AdminCreateIncidenciaBody {
  title: string;
  description: string;
  category: string;
  /** Omitido = 'media' (el default de la columna). Ver config/priority.ts. */
  priority?: string;
  source: 'phone' | 'email' | 'in_person';
  reporterName?: string;
  reporterPhone?: string;
  reporterEmail?: string;
  address?: string;
  assignedAreaId?: string;
  assignedTo?: string;
}

export interface Incidencia {
  id: string;
  /** Referencia visible del ticket: `KV-2608-4H7Q`. El correlativo interno
   *  (`number`) NO se expone — filtraría cuántos tickets hay en total. */
  reference: string;
  userId: string | null;
  title: string;
  description: string;
  category: string;
  status: string;
  /** 'baja' | 'media' | 'alta' | 'critica'. Ver config/priority.ts. */
  priority: string;
  source: string;
  reporterName: string | null;
  reporterPhone: string | null;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  photoPath: string | null;
  /** Adjuntos: capturas incluidas desde el paso 34 (ver `esImagen`). */
  files: TicketFile[];
  adminNote: string | null;
  assignedTo: string | null;
  assignedAreaId: string | null;
  assigneeName: string | null;
  assignedAreaName: string | null;
  /** El cliente del ticket, copiado del dueño al crearlo (paso 85a). */
  clienteId?: string | null;
  /** Su nombre (85b). Null = ticket sin cliente (anterior al 85a, o huérfano). */
  clienteNombre?: string | null;
  /** Ticket de un cliente desactivado (K53): solo se puede cerrar. */
  frozen?: boolean;
  /** SLA de primera respuesta (paso 89). Solo llega a staff; para quien
   *  reporta, todo null. `firstResponseWarnAt` lo calcula el servidor con el
   *  reloj laborable, que es UNO: el front no cuenta horas laborables. */
  slaPackNombre?: string | null;
  slaPlazoMin?: number | null;
  firstResponseDueAt?: string | null;
  firstResponseWarnAt?: string | null;
  firstResponseAt?: string | null;
  /** true cumplido · false incumplido · null todavía no cuenta, o no cuenta
   *  nunca (rechazado, o resuelto a tiempo sin responder). */
  slaCumplido?: boolean | null;
  createdAt: string;
  updatedAt: string;
  statusHistory: StatusHistoryEntry[];
}

/** Adjunto del ticket. Una captura es uno más desde el paso 34 (K22): se
 *  distingue por `mediaType` (ver `esImagen`), que el backend comprobó por la
 *  firma del fichero, no por lo que declaró quien lo subió.
 *  `messageId` null = llegó con el ALTA, junto a la descripción. */
export interface TicketFile {
  id: string;
  filename: string;
  mediaType: string;
  sizeBytes: number;
  source: 'requester' | 'staff';
  messageId: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

/** Las que se pintan como miniatura. Una imagen de otro tipo (GIF, HEIC…) se
 *  guarda y se descarga, pero no se pinta: no se comprobó su firma. */
export function esImagen(f: Pick<TicketFile, 'mediaType'>): boolean {
  return f.mediaType === 'image/png' || f.mediaType === 'image/jpeg';
}

/** Adjuntos por mensaje: el panel admite 10, el portal 5 (paso 34, D2 y D3).
 *  El backend manda; esto solo avisa antes de enviar. */
export const MAX_ADJUNTOS_PANEL = 10;
export const MAX_ADJUNTOS_PORTAL = 5;

export interface StatusHistoryEntry {
  id: string;
  oldStatus: string | null;
  newStatus: string;
  changedBy: string;
  note: string | null;
  createdAt: string;
}

export interface IncidenciaList {
  items: Incidencia[];
  total: number;
  page: number;
  limit: number;
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  sortOrder: number;
  defaultAreaId: string | null;
}

export interface Area {
  id: string;
  name: string;
  description: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InternalUser {
  id: string;
  email: string;
  displayName: string | null;
  roleId: string;
  roleName: string;
  isActive: boolean;
  areas: { id: string; name: string }[];
  /** Tickets abiertos que lleva (paso 85a): se avisa ANTES de desactivarle. */
  openTickets: number;
  createdAt: string;
}

export interface RolePermission {
  id: string;
  module: string;
  action: string;
  description: string;
}

/** Una PERSONA de un cliente (cuenta `requester`). */
export interface Requester {
  id: string;
  email: string;
  displayName: string | null;
  isEmailVerified: boolean;
  /** La casilla de ESTA persona. */
  isActive: boolean;
  /** Si puede entrar: además depende de su cliente (paso 85a). «Activo» con el
   *  cliente desactivado NO entra, y la pantalla tiene que decirlo. */
  isLive: boolean;
  /** null = cuenta anterior al 85a, «sin cliente». */
  clienteId: string | null;
  clienteNombre: string | null;
  createdAt: string;
}

/** El cliente: la empresa a la que Adaptia da soporte (paso 85a). ⚠️ No es
 *  `Area`, que es un equipo INTERNO. */
export interface Cliente {
  id: string;
  nombre: string;
  cif: string | null;
  contactoNombre: string | null;
  telefono: string | null;
  email: string | null;
  notas: string | null;
  isActive: boolean;
  personas: number;
  ticketsAbiertos: number;
  /** Pack de SLA (paso 89). null = el estándar; el nombre llega resuelto. */
  slaPackId: string | null;
  slaPackNombre: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoleWithPermissions {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: RolePermission[];
  /** Cuentas que pueden entrar con este rol (activas y de un cliente activo). */
  activeUsers: number;
}

// ── Incidencias ──────────────────────────────────────────────────────

/**
 * Pide el enlace mágico del portal. **Sin token**: es una llamada de la puerta,
 * antes de que haya sesión.
 *
 * ⚠️ El `message` lo escribe el backend y se pinta TAL CUAL. Es el mismo
 * exista la cuenta o no, sea de cliente o de staff — si el front lo
 * interpretara, la puerta se convertiría en un listado de con quién trabaja
 * Adaptia. Hay un test en el backend que compara las respuestas enteras.
 *
 * Tampoco se mira el código de estado para decidir qué contar: un 202 y un
 * error de red dicen lo mismo a quien está delante.
 */
/* ── Portal del cliente ────────────────────────────────────────────
 * Desde que hay una sola app (paso 38), el portal vive dentro del admin y usa
 * este mismo cliente. Son los MISMOS endpoints que usaba `apps/portal`, no
 * unos nuevos: `/incidencias/mine` ya filtra por quien pregunta y
 * `/messages/portal` es la vía de respuesta del cliente.
 *
 * ⚠️ NO se usan aquí los endpoints de staff. `/incidencias` a secas devuelve
 * la bandeja entera para quien tiene `view_all`; un requester recibiría solo
 * lo suyo, sí, pero apoyarse en eso es apoyarse en que el filtro del router no
 * cambie nunca. `mine` dice en su nombre lo que hace. */

export interface PortalTicket {
  id: string;
  reference: string;
  title: string;
  description: string;
  status: string;
  category: string;
  createdAt: string;
  /** Solo los que el cliente puede ver: los de una nota interna no llegan
   *  (paso 34, `thread.py::adjunto_publicable`). */
  files: TicketFile[];
}

export interface PortalMessage {
  id: string;
  authorType: string;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export async function listMyTickets(token: string): Promise<{ items: PortalTicket[] }> {
  const resp = await fetch(`${BASE}/incidencias/mine?limit=100`, { headers: authHeaders(token) });
  return handleResponse<{ items: PortalTicket[] }>(resp);
}

export async function getMyTicket(token: string, id: string): Promise<PortalTicket> {
  const resp = await fetch(`${BASE}/incidencias/${id}`, { headers: authHeaders(token) });
  return handleResponse<PortalTicket>(resp);
}

export async function getMyThread(token: string, id: string): Promise<PortalMessage[]> {
  const resp = await fetch(`${BASE}/incidencias/${id}/messages`, { headers: authHeaders(token) });
  return handleResponse<PortalMessage[]>(resp);
}

export async function replyAsClient(
  token: string,
  id: string,
  body: string,
  adjuntos: File[] = [],
): Promise<PortalMessage> {
  const resp = await fetch(`${BASE}/incidencias/${id}/messages/portal`, {
    method: 'POST',
    ...cuerpoConAdjuntos(token, { body }, adjuntos),
  });
  return handleResponse<PortalMessage>(resp);
}

/** Abrir un ticket desde el portal. El endpoint es el general de alta y
 *  acepta a un `requester` (pide email verificado, no permisos de staff). */
export async function createTicketAsClient(
  token: string,
  body: {
    title: string;
    description: string;
    category: string;
    /** La pregunta al asistente desde la que se abre (92a): liga el KPI. */
    asistente_pregunta_id?: string;
  },
  adjuntos: File[] = [],
): Promise<PortalTicket> {
  const resp = await fetch(`${BASE}/incidencias`, {
    method: 'POST',
    ...cuerpoConAdjuntos(token, body, adjuntos),
  });
  return handleResponse<PortalTicket>(resp);
}

export async function requestPortalLink(email: string): Promise<{ message: string }> {
  const res = await fetch(`${BASE}/auth/portal/link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => null);
  return { message: data?.message ?? 'Si esa dirección tiene acceso, te llegará un enlace para entrar.' };
}

export async function adminCreateIncidencia(
  body: AdminCreateIncidenciaBody,
  token: string,
  adjuntos: File[] = [],
): Promise<Incidencia> {
  const resp = await fetch(`${BASE}/incidencias/admin`, {
    method: 'POST',
    ...cuerpoConAdjuntos(token, body, adjuntos),
  });
  return handleResponse<Incidencia>(resp);
}

export async function listAllIncidencias(
  token: string,
  params: { status?: string; category?: string; priority?: string; assignedAreaId?: string; assignedTo?: string; page?: number; limit?: number } = {},
): Promise<IncidenciaList> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.category) qs.set('category', params.category);
  if (params.priority) qs.set('priority', params.priority);
  if (params.assignedAreaId) qs.set('assigned_area_id', params.assignedAreaId);
  if (params.assignedTo) qs.set('assigned_to', params.assignedTo);
  if (params.page) qs.set('page', String(params.page));
  qs.set('limit', String(params.limit ?? 50));
  const resp = await fetch(`${BASE}/incidencias?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<IncidenciaList>(resp);
}

export async function getIncidencia(id: string, token: string): Promise<Incidencia> {
  const resp = await fetch(`${BASE}/incidencias/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Incidencia>(resp);
}

export async function changeStatus(
  id: string,
  status: string,
  note: string | null,
  token: string,
): Promise<Incidencia> {
  const resp = await fetch(`${BASE}/incidencias/${id}/status`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ status, note }),
  });
  return handleResponse<Incidencia>(resp);
}

/**
 * Descarga un adjunto del ticket.
 *
 * No es un `<a href>` porque el endpoint EXIGE cabecera Authorization y una
 * etiqueta `<a>` no puede llevarla — al contrario que las fotos, que se sirven
 * sin autenticar justo por esa limitación de `<img src>`. Aquí se descarga con
 * fetch y se dispara el guardado desde un blob, así el token nunca viaja en la
 * URL (donde acabaría en logs de proxy y en el historial del navegador).
 */
export async function downloadTicketFile(
  incidenciaId: string,
  file: TicketFile,
  token: string,
): Promise<void> {
  const resp = await fetch(`${BASE}/incidencias/${incidenciaId}/files/${file.id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error('No se pudo descargar el adjunto');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Sin esto el blob se queda en memoria hasta recargar la página.
  URL.revokeObjectURL(url);
}

export async function deleteTicketFile(
  incidenciaId: string,
  fileId: string,
  token: string,
): Promise<void> {
  const resp = await fetch(`${BASE}/incidencias/${incidenciaId}/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok && resp.status !== 204) throw new Error('No se pudo borrar el adjunto');
}

/**
 * Marca (o desmarca) qué respuesta resolvió el ticket.
 *
 * Se hace a posteriori a propósito: cuando escribes la respuesta no sabes aún
 * si resolverá nada — lo sabes cuando el usuario contesta "ya funciona". Es
 * excluyente por ticket, así que marcar una desmarca las demás.
 */
export async function markMessageResolutory(
  incidenciaId: string,
  messageId: string,
  resolutory: boolean,
  token: string,
): Promise<void> {
  const resp = await fetch(
    `${BASE}/incidencias/${incidenciaId}/messages/${messageId}/resolutory`,
    { method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ resolutory }) },
  );
  await handleResponse(resp);
}

export async function changePriority(
  id: string,
  priority: string,
  token: string,
): Promise<Incidencia> {
  const resp = await fetch(`${BASE}/incidencias/${id}/priority`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ priority }),
  });
  return handleResponse<Incidencia>(resp);
}

export async function assignIncidencia(
  id: string,
  assignedTo: string | null,
  assignedAreaId: string | null,
  token: string,
): Promise<Incidencia> {
  const resp = await fetch(`${BASE}/incidencias/${id}/assign`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ assignedTo, assignedAreaId }),
  });
  return handleResponse<Incidencia>(resp);
}

/** URL de un adjunto. Se pide SIEMPRE con `fetch` y la cabecera (ver
 *  `AuthedImage`): nunca va en un `<img src>` ni lleva el token. */
export function getFileUrl(incidenciaId: string, fileId: string): string {
  return `${BASE}/incidencias/${incidenciaId}/files/${fileId}`;
}

/**
 * Entrada del relato del ticket. `/timeline` devuelve las tres fuentes ya
 * fusionadas y ordenadas: eventos de estado, asignaciones y mensajes del hilo.
 *
 * `message` cubre tanto respuestas al usuario como notas internas — se
 * distinguen por `details.visibility`. El backend ya filtra las internas para
 * quien no es staff, así que aquí no hace falta comprobarlo otra vez.
 *
 * `note_update` es solo rastro histórico de cuando las notas vivían en
 * `admin_note`: no lleva contenido.
 */
export interface TimelineEvent {
  type: 'status_change' | 'assignment' | 'note_update' | 'created' | 'message';
  createdAt: string;
  actorName: string | null;
  details: Record<string, unknown>;
}

export async function getIncidenciaTimeline(
  id: string,
  token: string,
): Promise<TimelineEvent[]> {
  const resp = await fetch(`${BASE}/incidencias/${id}/timeline`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<TimelineEvent[]>(resp);
}

// ── Hilo de mensajes del ticket ──────────────────────────────────────
//
// El hilo es siempre CARA AL USUARIO que reporta — nunca notas internas (esas
// van por updateAdminNote). El mismo hilo recibirá los correos entrantes
// cuando entre el relay, con channel: 'email'.

export interface TicketMessage {
  id: string;
  /** inbound = del usuario que reporta · outbound = de soporte */
  direction: 'inbound' | 'outbound';
  channel: 'app' | 'email' | 'portal';
  authorType: 'user' | 'staff' | 'system';
  authorId: string | null;
  authorName: string | null;
  body: string;
  /** public = lo ve quien reporta · internal = solo el equipo */
  visibility: 'public' | 'internal';
  isResolutory: boolean;
  createdAt: string;
}

export async function listTicketMessages(
  id: string,
  token: string,
): Promise<TicketMessage[]> {
  const resp = await fetch(`${BASE}/incidencias/${id}/messages`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<TicketMessage[]>(resp);
}

export async function createTicketMessage(
  id: string,
  body: string,
  token: string,
  visibility: TicketMessage['visibility'] = 'internal',
  isResolutory = false,
  /** Tiempo dedicado que se apunta CON el mensaje (paso 93), en el mismo
   *  commit: o entran los dos o ninguno. */
  minutes?: number,
  /** Adjuntos del mensaje (paso 34), en el MISMO envío: los de una nota son
   *  tan internos como ella. */
  adjuntos: File[] = [],
): Promise<TicketMessage> {
  const resp = await fetch(`${BASE}/incidencias/${id}/messages`, {
    method: 'POST',
    ...cuerpoConAdjuntos(
      token, { body, visibility, isResolutory, ...(minutes ? { minutes } : {}) }, adjuntos,
    ),
  });
  return handleResponse<TicketMessage>(resp);
}

// ── Tiempo dedicado (paso 93) ─────────────────────────────────────────
// Interno: el backend da 404 a quien no es staff. Los minutos son EXACTOS; el
// redondeo es del contrato y del informe (88), no de aquí.

export interface Worklog {
  id: string;
  userId: string;
  userName: string | null;
  messageId: string | null;
  minutes: number;
  /** Fecha del TRABAJO (YYYY-MM-DD), no la del apunte. */
  workedOn: string;
  billable: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  /** El autor, o quien tiene `incidencias:assign`. Lo decide el backend. */
  canEdit: boolean;
}

export interface WorklogList {
  items: Worklog[];
  totalMinutes: number;
  billableMinutes: number;
}

export interface WorklogInput {
  minutes: number;
  /** Omitido = hoy (en Madrid, lo pone el servidor). */
  workedOn?: string;
  billable: boolean;
  note?: string | null;
}

export async function listWorklogs(id: string, token: string): Promise<WorklogList> {
  const resp = await fetch(`${BASE}/incidencias/${id}/worklogs`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<WorklogList>(resp);
}

export async function createWorklog(id: string, input: WorklogInput, token: string): Promise<Worklog> {
  const resp = await fetch(`${BASE}/incidencias/${id}/worklogs`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  return handleResponse<Worklog>(resp);
}

export async function updateWorklog(
  id: string,
  worklogId: string,
  input: WorklogInput,
  token: string,
): Promise<Worklog> {
  const resp = await fetch(`${BASE}/incidencias/${id}/worklogs/${worklogId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  return handleResponse<Worklog>(resp);
}

export async function deleteWorklog(id: string, worklogId: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/incidencias/${id}/worklogs/${worklogId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

// ── Areas ────────────────────────────────────────────────────────────

export async function listAreas(token: string): Promise<Area[]> {
  const resp = await fetch(`${BASE}/admin/areas`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Area[]>(resp);
}

export interface AreaInput {
  name?: string;
  description?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isActive?: boolean;
}

export async function createArea(
  data: AreaInput & { name: string },
  token: string,
): Promise<Area> {
  const resp = await fetch(`${BASE}/admin/areas`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Area>(resp);
}

export async function updateArea(
  id: string,
  data: AreaInput,
  token: string,
): Promise<Area> {
  const resp = await fetch(`${BASE}/admin/areas/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Area>(resp);
}

export async function deactivateArea(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/areas/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

// ── Users ────────────────────────────────────────────────────────────

export interface UserLookup {
  exists: boolean;
  displayName: string | null;
  isStaff: boolean;
  isActive: boolean;
  /** De qué cliente es (85b): el ticket nacerá con ese cliente. */
  clienteNombre?: string | null;
}

/** ¿Tiene cuenta esta dirección? Lo pregunta el alta manual de tickets.
 *
 * ⚠️ No es comodidad: el alta guarda `reporterEmail` como texto y el ticket
 * nace huérfano. Quien lo ata a una persona es la adopción del portal, que casa
 * por ese correo — así que una dirección sin cuenta significa un ticket que el
 * cliente no verá nunca, sin error y sin aviso.
 */
export async function lookupUserByEmail(email: string, token: string): Promise<UserLookup> {
  const resp = await fetch(`${BASE}/admin/users/lookup?email=${encodeURIComponent(email)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<UserLookup>(resp);
}

export async function listRequesters(token: string, clienteId?: string): Promise<Requester[]> {
  const qs = clienteId ? `?cliente_id=${encodeURIComponent(clienteId)}` : '';
  const resp = await fetch(`${BASE}/admin/users/requesters${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Requester[]>(resp);
}

/** Alta de una PERSONA de cliente — el ÚNICO camino (pasos 96 y 85a), se
 * llegue desde Clientes, desde Usuarios o desde el alta de un ticket.
 *
 * No lleva contraseña: al portal se entra con un enlace al buzón. Con
 * `sendAccess` (por defecto) el backend manda ese primer enlace al crear la
 * cuenta; sin él, se manda después con `sendRequesterAccess`. Ojo: aun sin ese
 * correo la cuenta YA existe, así que sus correos a soporte abren ticket.
 */
export async function createRequester(
  body: { email: string; displayName: string; clienteId: string; sendAccess?: boolean },
  token: string,
): Promise<Requester> {
  const resp = await fetch(`${BASE}/admin/users/requesters`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return handleResponse<Requester>(resp);
}

/** Corregir o desactivar a una persona (85a). Cambiar de cliente NO mueve sus
 *  tickets viejos, salvo los que no tenían cliente (cuentas anteriores al 85a). */
export async function updateRequester(
  id: string,
  data: { email?: string; displayName?: string; clienteId?: string; isActive?: boolean },
  token: string,
): Promise<Requester> {
  const resp = await fetch(`${BASE}/admin/users/requesters/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Requester>(resp);
}

/** Manda (otra vez) la bienvenida con el enlace del portal. */
export async function sendRequesterAccess(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/users/requesters/${id}/access`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function listInternalUsers(
  token: string,
  params: { roleName?: string; areaId?: string } = {},
): Promise<InternalUser[]> {
  const qs = new URLSearchParams();
  if (params.roleName) qs.set('role_name', params.roleName);
  if (params.areaId) qs.set('area_id', params.areaId);
  const resp = await fetch(`${BASE}/admin/users?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<InternalUser[]>(resp);
}

export async function createInternalUser(
  data: { email: string; displayName: string; password: string; roleId: string; areaIds: string[] },
  token: string,
): Promise<InternalUser> {
  const resp = await fetch(`${BASE}/admin/users`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<InternalUser>(resp);
}

export async function updateInternalUser(
  id: string,
  data: { email?: string; displayName?: string; password?: string; roleId?: string; areaIds?: string[]; isActive?: boolean },
  token: string,
): Promise<InternalUser> {
  const resp = await fetch(`${BASE}/admin/users/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<InternalUser>(resp);
}

/** 409 `USER_HAS_OPEN_TICKETS` (paso 85a): el técnico lleva tickets abiertos y
 *  hay que decir qué hacer con ellos antes de desactivarle. */
export class UserHasOpenTicketsError extends Error {
  constructor(message: string, public openTickets: number) {
    super(message);
  }
}

/** Desactiva a un TÉCNICO. Con tickets abiertos hay que elegir: pasárselos a
 *  otro (`reassign` + `reassignTo`) o dejarlos sin asignar (`unassign`). */
export async function deactivateUser(
  id: string,
  token: string,
  openTickets?: { mode: 'reassign'; reassignTo: string } | { mode: 'unassign' },
): Promise<void> {
  const body = openTickets
    ? { openTickets: openTickets.mode, reassignTo: openTickets.mode === 'reassign' ? openTickets.reassignTo : null }
    : undefined;
  const resp = await fetch(`${BASE}/admin/users/${id}/deactivate`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (resp.status === 409) {
    const b = await resp.json().catch(() => null);
    throw new UserHasOpenTicketsError(b?.detail?.message ?? 'Tiene tickets abiertos.', b?.detail?.openTickets ?? 0);
  }
  return handleResponse<void>(resp);
}

// ── Clientes (paso 85a) ──────────────────────────────────────────────

export interface ClienteInput {
  nombre?: string;
  cif?: string | null;
  contactoNombre?: string | null;
  telefono?: string | null;
  email?: string | null;
  notas?: string | null;
  isActive?: boolean;
  /** Cambiarlo pide `sla:manage` (paso 89). null = el estándar. */
  slaPackId?: string | null;
}

export async function listClientes(token: string): Promise<Cliente[]> {
  const resp = await fetch(`${BASE}/admin/clientes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Cliente[]>(resp);
}

export async function createCliente(data: ClienteInput & { nombre: string }, token: string): Promise<Cliente> {
  const resp = await fetch(`${BASE}/admin/clientes`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Cliente>(resp);
}

export async function updateCliente(id: string, data: ClienteInput, token: string): Promise<Cliente> {
  const resp = await fetch(`${BASE}/admin/clientes/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Cliente>(resp);
}

/** 409 `CLIENTE_HAS_HISTORY`: tiene personas o tickets y no se borra — se
 *  desactiva. Clase propia por lo mismo que `UserHasHistoryError`. */
export class ClienteHasHistoryError extends Error {}

export async function deleteCliente(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/clientes/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    throw new ClienteHasHistoryError(body?.detail?.message ?? 'El cliente tiene historial y no se puede borrar.');
  }
  return handleResponse<void>(resp);
}

// ── Packs de SLA (paso 89) ──────────────────────────────────────────

export interface SlaPack {
  id: string;
  nombre: string;
  esEstandar: boolean;
  isActive: boolean;
  minutosCritica: number;
  minutosAlta: number;
  minutosMedia: number;
  minutosBaja: number;
  /** ISO: 1 = lunes … 7 = domingo. */
  dias: number[];
  /** «HH:MM» en la zona de la instalación. */
  horaInicio: string;
  horaFin: string;
  /** Clientes que lo usan (al estándar se le suman los que no tienen pack). */
  clientes: number;
  createdAt: string;
  updatedAt: string;
}

export type SlaPackInput = Pick<
  SlaPack,
  'nombre' | 'minutosCritica' | 'minutosAlta' | 'minutosMedia' | 'minutosBaja' | 'dias' | 'horaInicio' | 'horaFin'
> & { isActive?: boolean };

export async function listSlaPacks(token: string): Promise<SlaPack[]> {
  const resp = await fetch(`${BASE}/admin/sla-packs`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<SlaPack[]>(resp);
}

export async function createSlaPack(data: SlaPackInput, token: string): Promise<SlaPack> {
  const resp = await fetch(`${BASE}/admin/sla-packs`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<SlaPack>(resp);
}

export async function updateSlaPack(id: string, data: SlaPackInput, token: string): Promise<SlaPack> {
  const resp = await fetch(`${BASE}/admin/sla-packs/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ isActive: true, ...data }),
  });
  return handleResponse<SlaPack>(resp);
}

/** 409 `PACK_HAS_HISTORY`: lo usan clientes o tickets — se desactiva. */
export class SlaPackHasHistoryError extends Error {}

export async function deleteSlaPack(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/sla-packs/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    throw new SlaPackHasHistoryError(body?.detail?.message ?? 'El pack está en uso y no se puede borrar.');
  }
  return handleResponse<void>(resp);
}

// ── Festivos del SLA (paso 89b) ─────────────────────────────────────

export type FestivoAmbito = 'nacional' | 'autonomico' | 'local';

export interface Festivo {
  id: string;
  /** «AAAA-MM-DD», el día en la zona de la instalación. */
  fecha: string;
  nombre: string;
  ambito: FestivoAmbito;
  createdAt: string;
}

export async function listFestivos(token: string): Promise<Festivo[]> {
  const resp = await fetch(`${BASE}/admin/festivos`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Festivo[]>(resp);
}

export async function createFestivo(
  data: Pick<Festivo, 'fecha' | 'nombre' | 'ambito'>, token: string,
): Promise<Festivo> {
  const resp = await fetch(`${BASE}/admin/festivos`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Festivo>(resp);
}

export async function deleteFestivo(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/festivos/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

/** 409 `USER_HAS_HISTORY` de `DELETE /admin/users/{id}` (K41, paso 12a): la
 *  cuenta tiene tickets o actividad y no se borra — se desactiva. Es una clase
 *  propia para que la pantalla ofrezca desactivar en vez de pintar un error. */
export class UserHasHistoryError extends Error {}

export async function deleteRequester(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/users/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    throw new UserHasHistoryError(body?.detail?.message ?? 'La cuenta tiene historial y no se puede borrar.');
  }
  return handleResponse<void>(resp);
}

// ── Roles & Permissions ──────────────────────────────────────────────

export async function listRoles(token: string): Promise<RoleWithPermissions[]> {
  const resp = await fetch(`${BASE}/admin/roles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<RoleWithPermissions[]>(resp);
}

export async function listAllPermissions(token: string): Promise<RolePermission[]> {
  const resp = await fetch(`${BASE}/admin/roles/permissions/all`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<RolePermission[]>(resp);
}

/** Guarda los permisos de VARIOS roles: todos o ninguno (paso 48a). Cada
 *  entrada lleva el conjunto entero que queda, no un diff. Devuelve todos los
 *  roles ya guardados. */
export async function updateRolesPermissions(
  roles: { roleId: string; permissionIds: string[] }[],
  token: string,
): Promise<RoleWithPermissions[]> {
  const resp = await fetch(`${BASE}/admin/roles/permissions`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ roles }),
  });
  return handleResponse<RoleWithPermissions[]>(resp);
}

// ── Form Builder ────────────────────────────────────────────────────

export interface FieldOptionApi {
  label: string;
  value: string;
}

export interface FieldValidationApi {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
}

export interface FieldDefinitionApi {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  validation: FieldValidationApi;
  options?: FieldOptionApi[];
  defaultValue?: unknown;
  richtext?: boolean;
}

export type FormType = 'standalone' | 'course_survey' | 'process_step';

export interface FormDefinitionApi {
  id: string;
  slug: string;
  module: string | null;
  title: string;
  titleEu: string | null;
  description: string | null;
  descriptionEu: string | null;
  version: number;
  status: string;
  formType: FormType;
  fields: FieldDefinitionApi[];
  createdBy: string;
  parentId: string | null;
  courseId: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  closesAt: string | null;
  isTemplate: boolean;
  hasCoverImage: boolean;
  requiresAuth: boolean;
}

export interface FormDefinitionList {
  items: FormDefinitionApi[];
  total: number;
}

export interface FormSubmissionApi {
  id: string;
  formDefinitionId: string;
  formVersion: number;
  userId: string | null;
  data: Record<string, unknown>;
  submittedAt: string;
  reviewStatus: 'pendiente' | 'aprobada' | 'rechazada';
  reviewNote: string | null;
}

export interface FormSubmissionList {
  items: FormSubmissionApi[];
  total: number;
}

export async function listFormDefinitions(token: string, module?: string): Promise<FormDefinitionList> {
  const qs = module ? `?module=${encodeURIComponent(module)}` : '';
  const resp = await fetch(`${BASE}/forms${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<FormDefinitionList>(resp);
}

export async function listFormTemplates(token: string, module?: string): Promise<FormDefinitionList> {
  const qs = module ? `?module=${encodeURIComponent(module)}` : '';
  const resp = await fetch(`${BASE}/forms/templates${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<FormDefinitionList>(resp);
}

export async function createFormDefinition(
  data: { slug?: string; title: string; titleEu?: string | null; description?: string; descriptionEu?: string | null; fields?: FieldDefinitionApi[]; module?: string; formType?: FormType; closesAt?: string; isTemplate?: boolean; fromTemplateId?: string; requiresAuth?: boolean },
  token: string,
): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function getFormDefinition(id: string, token: string): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function updateFormDefinition(
  id: string,
  data: { title?: string; titleEu?: string | null; description?: string; descriptionEu?: string | null; fields?: FieldDefinitionApi[]; closesAt?: string | null; requiresAuth?: boolean },
  token: string,
): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function publishForm(id: string, token: string): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}/publish`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function unpublishForm(id: string, token: string): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}/unpublish`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function createNewVersion(id: string, token: string): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}/new-version`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function deleteFormDefinition(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/forms/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function listFormSubmissions(formId: string, token: string): Promise<FormSubmissionList> {
  const resp = await fetch(`${BASE}/forms/${formId}/submissions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<FormSubmissionList>(resp);
}

// ── Facilities & Spaces ─────────────────────────────────────────────

export interface FacilityApi {
  id: string;
  name: string;
  address: string | null;
  area: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  spaces: SpaceSummaryInFacility[];
}

export interface SpaceSummaryInFacility {
  id: string;
  name: string;
  spaceType: string;
  capacity: number;
  isActive: boolean;
}

export interface FacilityList {
  items: FacilityApi[];
  total: number;
}

export interface SpaceApi {
  id: string;
  facilityId: string;
  facility: { id: string; name: string; area: string } | null;
  name: string;
  description: string | null;
  spaceType: string;
  capacity: number;
  amenities: string[] | null;
  color: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SpaceList {
  items: SpaceApi[];
  total: number;
}

export interface BookingApi {
  id: string;
  spaceId: string;
  title: string;
  bookedDate: string;
  timeStart: string;
  timeEnd: string;
  status: string;
  category: string;
  notes: string | null;
  courseId: string | null;
  recurrenceId: string | null;
  bookedBy: string;
  booker: { id: string; displayName: string | null; email: string } | null;
  createdAt: string;
}

export interface BookingList {
  items: BookingApi[];
  total: number;
}

export interface RecurrenceResult {
  id: string;
  spaceId: string;
  title: string;
  weekday: number;
  timeStart: string;
  timeEnd: string;
  startDate: string;
  endDate: string;
  bookingsCreated: number;
}

// Facilities
export async function listFacilities(token: string, area?: string): Promise<FacilityList> {
  const qs = area ? `?area=${encodeURIComponent(area)}` : '';
  const resp = await fetch(`${BASE}/admin/spaces/facilities${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<FacilityList>(resp);
}

export async function createFacility(data: { name: string; address?: string; area?: string; description?: string }, token: string): Promise<FacilityApi> {
  const resp = await fetch(`${BASE}/admin/spaces/facilities`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<FacilityApi>(resp);
}

export async function updateFacility(id: string, data: Record<string, unknown>, token: string): Promise<FacilityApi> {
  const resp = await fetch(`${BASE}/admin/spaces/facilities/${id}`, { method: 'PUT', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<FacilityApi>(resp);
}

export async function deleteFacility(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/spaces/facilities/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<void>(resp);
}

// Spaces
export async function listSpaces(token: string, facilityId?: string): Promise<SpaceList> {
  const params = new URLSearchParams();
  if (facilityId) params.set('facilityId', facilityId);
  const qs = params.toString() ? `?${params}` : '';
  const resp = await fetch(`${BASE}/admin/spaces${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<SpaceList>(resp);
}

export async function createSpace(data: { facilityId: string; name: string; description?: string; spaceType?: string; capacity: number; amenities?: string[]; color?: string }, token: string): Promise<SpaceApi> {
  const resp = await fetch(`${BASE}/admin/spaces`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<SpaceApi>(resp);
}

export async function updateSpace(id: string, data: Record<string, unknown>, token: string): Promise<SpaceApi> {
  const resp = await fetch(`${BASE}/admin/spaces/${id}`, { method: 'PUT', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<SpaceApi>(resp);
}

export async function deleteSpace(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/spaces/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<void>(resp);
}

// Bookings
export async function listBookings(token: string, opts?: { spaceId?: string; from?: string; to?: string }): Promise<BookingList> {
  const params = new URLSearchParams();
  if (opts?.spaceId) params.set('spaceId', opts.spaceId);
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  const qs = params.toString() ? `?${params}` : '';
  const resp = await fetch(`${BASE}/admin/spaces/bookings${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<BookingList>(resp);
}

export async function createBooking(data: { spaceId: string; title: string; bookedDate: string; timeStart: string; timeEnd: string; category?: string; notes?: string }, token: string): Promise<BookingApi> {
  const resp = await fetch(`${BASE}/admin/spaces/bookings`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<BookingApi>(resp);
}

export async function updateBooking(id: string, data: Record<string, unknown>, token: string): Promise<BookingApi> {
  const resp = await fetch(`${BASE}/admin/spaces/bookings/${id}`, { method: 'PUT', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<BookingApi>(resp);
}

export async function cancelBooking(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/spaces/bookings/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<void>(resp);
}

// Recurrences
export async function createRecurrence(data: { spaceId: string; title: string; weekday: number; timeStart: string; timeEnd: string; category?: string; startDate: string; endDate: string }, token: string): Promise<RecurrenceResult> {
  const resp = await fetch(`${BASE}/admin/spaces/recurrences`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });
  return handleResponse<RecurrenceResult>(resp);
}

export async function deleteRecurrence(id: string, token: string, futureOnly?: boolean): Promise<void> {
  const qs = futureOnly === false ? '?cancelFutureOnly=false' : '';
  const resp = await fetch(`${BASE}/admin/spaces/recurrences/${id}${qs}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  return handleResponse<void>(resp);
}

// ── Cursos ──────────────────────────────────────────────────────────

export interface EnrollmentCountsApi {
  total: number;
  pending: number;
  confirmed: number;
  waitlisted: number;
  cancelled: number;
  pendingPayment: number;
}

export interface SpaceSummaryApi {
  id: string;
  name: string;
  capacity: number;
}

export interface SessionApi {
  id: string;
  courseId: string;
  sessionNum: number;
  sessionDate: string;
  timeStart: string;
  timeEnd: string;
  title: string | null;
  content: string | null;
}

export interface SessionList {
  items: SessionApi[];
  total: number;
}

export interface SessionInput {
  sessionDate: string;
  timeStart: string;
  timeEnd: string;
  title?: string;
  content?: string;
}

export interface LotteryResult {
  confirmed: number;
  waitlisted: number;
  message: string;
}

export interface CourseApi {
  id: string;
  slug: string;
  title: string;
  titleEu: string | null;
  description: string | null;
  descriptionEu: string | null;
  courseType: string;
  eventLanguage: string | null;
  category: string | null;
  modality: string;
  onlineUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  location: string | null;
  spaceId: string | null;
  space: SpaceSummaryApi | null;
  instructorName: string | null;
  instructorBio: string | null;
  maxCapacity: number | null;
  enrollmentMode: string;
  waitlistMode: 'none' | 'fifo' | 'lottery';
  price: number;
  status: string;
  formDefinitionId: string | null;
  programText: string | null;
  programDocumentPath: string | null;
  surveyFormId: string | null;
  surveyMode: string;
  surveyWindowDays: number;
  surveySentAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  closesAt: string | null;
  enrollmentCounts: EnrollmentCountsApi | null;
  sessionCount: number;
  hasCoverImage: boolean;
}

export interface CourseList {
  items: CourseApi[];
  total: number;
}

export interface EnrollmentApi {
  id: string;
  courseId: string;
  userId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  paymentStatus: string;
  formData: Record<string, unknown> | null;
  cancelToken: string;
  position: number | null;
  enrolledAt: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  paidAt: string | null;
}

export interface EnrollmentList {
  items: EnrollmentApi[];
  total: number;
}

export async function listCursos(token: string, status?: string): Promise<CourseList> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const resp = await fetch(`${BASE}/cursos${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<CourseList>(resp);
}

export async function getCurso(id: string, token: string): Promise<CourseApi> {
  const resp = await fetch(`${BASE}/cursos/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<CourseApi>(resp);
}

export async function createCurso(
  data: {
    slug: string;
    title: string;
    titleEu?: string | null;
    description?: string;
    descriptionEu?: string | null;
    courseType?: string;
    eventLanguage?: string | null;
    category?: string;
    modality?: string;
    onlineUrl?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    spaceId?: string | null;
    instructorName?: string;
    instructorBio?: string;
    maxCapacity?: number | null;
    enrollmentMode?: string;
    waitlistMode?: 'none' | 'fifo' | 'lottery';
    price?: number;
    formDefinitionId?: string | null;
    programText?: string;
    closesAt?: string | null;
  },
  token: string,
): Promise<CourseApi> {
  const resp = await fetch(`${BASE}/cursos`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<CourseApi>(resp);
}

export interface UpdateCursoResult {
  ok: boolean;
  course?: CourseApi;
  /** Cuando el backend responde 409 CAPACITY_BELOW_CONFIRMED el admin
   *  puede reintentar con force=true para aceptar el sobreaforo. */
  capacityBelowConfirmed?: { newCapacity: number; currentConfirmed: number };
}

export async function updateCurso(
  id: string,
  data: {
    title?: string;
    titleEu?: string | null;
    description?: string;
    descriptionEu?: string | null;
    courseType?: string;
    eventLanguage?: string | null;
    category?: string;
    modality?: string;
    onlineUrl?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    spaceId?: string | null;
    instructorName?: string;
    instructorBio?: string;
    maxCapacity?: number | null;
    enrollmentMode?: string;
    waitlistMode?: 'none' | 'fifo' | 'lottery';
    price?: number;
    formDefinitionId?: string | null;
    programText?: string;
    closesAt?: string | null;
  },
  token: string,
  options?: { force?: boolean },
): Promise<UpdateCursoResult> {
  const resp = await fetch(`${BASE}/cursos/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ ...data, force: options?.force === true }),
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    const detail = body?.detail;
    if (detail?.code === 'CAPACITY_BELOW_CONFIRMED') {
      return {
        ok: false,
        capacityBelowConfirmed: {
          newCapacity: detail.newCapacity,
          currentConfirmed: detail.currentConfirmed,
        },
      };
    }
  }
  const course = await handleResponse<CourseApi>(resp);
  return { ok: true, course };
}

export async function publishCurso(id: string, token: string): Promise<CourseApi> {
  const resp = await fetch(`${BASE}/cursos/${id}/publish`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<CourseApi>(resp);
}

export async function closeCurso(id: string, token: string): Promise<CourseApi> {
  const resp = await fetch(`${BASE}/cursos/${id}/close`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<CourseApi>(resp);
}

export async function deleteCurso(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/cursos/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function listEnrollments(cursoId: string, token: string): Promise<EnrollmentList> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/enrollments`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<EnrollmentList>(resp);
}

export interface UpdateEnrollmentResult {
  ok: boolean;
  enrollment?: EnrollmentApi;
  /** When the backend rejects with 409 OVERCAPACITY the admin UI can prompt
   *  for an explicit override and retry with force=true. */
  overcapacity?: { currentConfirmed: number; maxCapacity: number };
}

export async function updateEnrollmentStatus(
  cursoId: string,
  enrollmentId: string,
  status: string,
  token: string,
  options?: { force?: boolean },
): Promise<UpdateEnrollmentResult> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/enrollments/${enrollmentId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ status, force: options?.force === true }),
  });
  if (resp.status === 409) {
    const body = await resp.json().catch(() => null);
    const detail = body?.detail;
    if (detail?.code === 'OVERCAPACITY') {
      return {
        ok: false,
        overcapacity: {
          currentConfirmed: detail.currentConfirmed,
          maxCapacity: detail.maxCapacity,
        },
      };
    }
  }
  const enrollment = await handleResponse<EnrollmentApi>(resp);
  return { ok: true, enrollment };
}

export async function markEnrollmentPaid(
  cursoId: string,
  enrollmentId: string,
  note: string | null,
  token: string,
): Promise<EnrollmentApi> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/enrollments/${enrollmentId}/mark-paid`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ note }),
  });
  return handleResponse<EnrollmentApi>(resp);
}

export async function runLottery(cursoId: string, token: string): Promise<LotteryResult> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/enrollments/lottery`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<LotteryResult>(resp);
}

export async function listSessions(cursoId: string, token: string): Promise<SessionList> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/sessions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<SessionList>(resp);
}

export async function saveSessions(cursoId: string, sessions: SessionInput[], token: string): Promise<SessionList> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/sessions`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ sessions }),
  });
  return handleResponse<SessionList>(resp);
}

export async function uploadProgramDocument(cursoId: string, file: File, token: string): Promise<{ programDocumentPath: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const resp = await fetch(`${BASE}/cursos/${cursoId}/program-document`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return handleResponse<{ programDocumentPath: string }>(resp);
}

export async function uploadCursoCoverImage(cursoId: string, file: File, token: string): Promise<{ hasCoverImage: boolean }> {
  const fd = new FormData();
  fd.append('file', file);
  const resp = await fetch(`${BASE}/cursos/${cursoId}/cover-image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  return handleResponse<{ hasCoverImage: boolean }>(resp);
}

export async function deleteCursoCoverImage(cursoId: string, token: string): Promise<{ hasCoverImage: boolean }> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/cover-image`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<{ hasCoverImage: boolean }>(resp);
}

export function cursoCoverImageUrl(cursoId: string): string {
  return `${BASE}/cursos/${cursoId}/cover-image`;
}

export interface ProgramAnalysis {
  title?: string;
  description?: string;
  category?: string;
  price?: number;
  location?: string;
  instructor?: string;
  sessions?: SessionInput[];
}

export async function analyzeProgramDocument(cursoId: string, token: string): Promise<ProgramAnalysis> {
  const resp = await fetch(`${BASE}/cursos/${cursoId}/analyze-program`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProgramAnalysis>(resp);
}

// ── Categories ───────────────────────────────────────────────────────

export async function listCategories(token: string): Promise<Category[]> {
  const resp = await fetch(`${BASE}/incidencias/categories`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<Category[]>(resp);
}

export async function createCategory(
  data: { name: string; icon: string; sortOrder: number; defaultAreaId?: string | null },
  token: string,
): Promise<Category> {
  const resp = await fetch(`${BASE}/incidencias/categories`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Category>(resp);
}

export async function updateCategory(
  id: string,
  data: { name?: string; icon?: string; sortOrder?: number; defaultAreaId?: string | null },
  token: string,
): Promise<Category> {
  const resp = await fetch(`${BASE}/incidencias/categories/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<Category>(resp);
}

export async function deleteCategory(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/incidencias/categories/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

// ── Course surveys ──────────────────────────────────────────────────

export interface SurveyStatusApi {
  mode: string; // 'none' | 'auto' | 'manual'
  windowDays: number;
  sentAt: string | null;
  formId: string | null;
  formSlug: string | null;
  formTitle: string | null;
  formStatus: string | null; // draft/published/archived
  totalEnrollments: number;
  responses: number;
  opened: number;
}

export interface SurveyLinkItemApi {
  enrollmentId: string;
  name: string | null;
  email: string | null;
  status: string;
  token: string;
  url: string;
  openedAt: string | null;
  completedAt: string | null;
}

export interface SurveyLinksApi {
  items: SurveyLinkItemApi[];
  windowOpensAt: string | null;
  windowClosesAt: string | null;
}

export interface AttachSurveyPayload {
  mode: 'none' | 'auto' | 'manual';
  windowDays?: number;
  source?: 'template' | 'custom' | 'existing';
  templateId?: string;
  existingFormId?: string;
  title?: string;
}

export async function getCourseSurveyStatus(courseId: string, token: string): Promise<SurveyStatusApi> {
  const resp = await fetch(`${BASE}/cursos/${courseId}/survey`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<SurveyStatusApi>(resp);
}

export async function attachCourseSurvey(
  courseId: string,
  payload: AttachSurveyPayload,
  token: string,
): Promise<SurveyStatusApi> {
  const resp = await fetch(`${BASE}/cursos/${courseId}/survey`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return handleResponse<SurveyStatusApi>(resp);
}

export async function sendCourseSurvey(courseId: string, token: string): Promise<SurveyStatusApi> {
  const resp = await fetch(`${BASE}/cursos/${courseId}/send-survey`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<SurveyStatusApi>(resp);
}

export async function getCourseSurveyLinks(courseId: string, token: string): Promise<SurveyLinksApi> {
  const resp = await fetch(`${BASE}/cursos/${courseId}/survey-links`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<SurveyLinksApi>(resp);
}

export async function processAutoSurveys(token: string): Promise<{ marked: number; courses: string[] }> {
  const resp = await fetch(`${BASE}/admin/surveys/process-auto`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<{ marked: number; courses: string[] }>(resp);
}

// ── Counters + activity (dashboard) ─────────────────────────────────

export interface CountersApi {
  incidenciasActive: number;
  incidenciasUnassigned: number;
  incidenciasResolvedToday: number;
  encuestasPublished: number;
  encuestasDraft: number;
  encuestaResponsesWeek: number;
  enrollmentsMonth: number;
  coursesActive: number;
  surveysLinkedToCourses: number;
}

export interface ActivityItemApi {
  at: string;
  kind: string;
  title: string;
  subtitle: string | null;
  severity: string; // red/amber/green/blue/neutral
}

export interface ActivityFeedApi {
  items: ActivityItemApi[];
}

export async function getCounters(token: string): Promise<CountersApi> {
  const resp = await fetch(`${BASE}/admin/counters`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<CountersApi>(resp);
}

export async function getActivity(token: string, limit = 10): Promise<ActivityFeedApi> {
  const resp = await fetch(`${BASE}/admin/activity?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ActivityFeedApi>(resp);
}

// ── Asistente proactivo (sugerencias / campana) ─────────────────────

export interface AssistantSuggestionApi {
  id: string;
  ruleKey: string;
  origin: string;
  urgency: string;          // low | medium | high
  title: string;
  context: string | null;
  proposedActionLabel: string | null;
  actionType: string | null;
  /** Parámetros de la acción (paso 51). En `responder_incidencia`, `borrador`
   *  (el texto de la pregunta frecuente que encaja, o vacío) y `faq_pregunta`. */
  actionParams: Record<string, unknown> | null;
  canExecute: boolean;
  entityType: string | null;
  entityId: string | null;
  status: string;           // nueva | hecha | descartada | mas_tarde
  snoozeUntil: string | null;
  resultText: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export type AssistantScope = 'pending' | 'snoozed' | 'resolved' | 'all';
export type AssistantResolveAction = 'done' | 'dismiss' | 'snooze';

export async function listAssistantSuggestions(
  token: string,
  scope: AssistantScope = 'pending',
): Promise<AssistantSuggestionApi[]> {
  const resp = await fetch(`${BASE}/admin/assistant/suggestions?scope=${scope}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await handleResponse<{ items: AssistantSuggestionApi[] }>(resp);
  return data.items;
}

export async function getAssistantCount(token: string): Promise<number> {
  const resp = await fetch(`${BASE}/admin/assistant/count`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await handleResponse<{ unread: number }>(resp);
  return data.unread;
}

export async function resolveAssistantSuggestion(
  token: string,
  id: string,
  action: AssistantResolveAction,
  snoozeHours?: number,
  payload?: Record<string, unknown>,
): Promise<AssistantSuggestionApi> {
  const body: Record<string, unknown> = { action };
  if (snoozeHours) body.snoozeHours = snoozeHours;
  if (payload) body.payload = payload;
  const resp = await fetch(`${BASE}/admin/assistant/suggestions/${id}/resolve`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return handleResponse<AssistantSuggestionApi>(resp);
}

export async function scanAssistant(token: string): Promise<{ created: number }> {
  const resp = await fetch(`${BASE}/admin/assistant/scan`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<{ created: number }>(resp);
}

/** Briefing con criterio del día (Wave 2.2). `summary` null = sin nada que
 *  destacar o LLM no disponible → el front usa su plantilla. */
export async function getAssistantBriefing(
  token: string,
): Promise<{ summary: string | null; generated: boolean }> {
  const resp = await fetch(`${BASE}/admin/assistant/briefing`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<{ summary: string | null; generated: boolean }>(resp);
}

// ── Composer conversacional (dos vías, Fase 2 · modelo 7a) ──────────

/** Acción de escritura que el agente propone y el técnico confirma. Reutiliza
 *  los ejecutores del backend (asignar_consulta, responder_consulta). */
export interface AssistantActionCard {
  actionType: string;
  label: string;
  summary: string | null;
  entityType: string | null;
  entityId: string | null;
  params: Record<string, unknown>;
}

export interface AssistantThreadMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions: AssistantActionCard[];
  createdAt: string;
}

/** Un hilo «Mis consultas»: una conversación que abre el técnico. Se ancla
 *  arriba de la bandeja y puede guardarse. status: active | saved | closed. */
export interface AssistantThread {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  messages: AssistantThreadMessage[];
}

export interface AssistantThreadSummary {
  id: string;
  title: string;
  status: string;
  updatedAt: string;
  messageCount: number;
  lastPreview: string | null;
}

export type AssistantThreadScope = 'open' | 'saved' | 'all';

/** Abre una consulta nueva: crea el hilo, corre el agente y devuelve la
 *  conversación (con las action cards del turno del asistente). */
export async function createAssistantThread(
  token: string,
  message: string,
): Promise<AssistantThread> {
  const resp = await fetch(`${BASE}/admin/assistant/threads`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ message }),
  });
  return handleResponse<AssistantThread>(resp);
}

/** Continúa un hilo existente con un nuevo turno del técnico. */
export async function postAssistantThreadMessage(
  token: string,
  threadId: string,
  message: string,
): Promise<AssistantThread> {
  const resp = await fetch(`${BASE}/admin/assistant/threads/${threadId}/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ message }),
  });
  return handleResponse<AssistantThread>(resp);
}

export async function listAssistantThreads(
  token: string,
  scope: AssistantThreadScope = 'open',
): Promise<AssistantThreadSummary[]> {
  const resp = await fetch(`${BASE}/admin/assistant/threads?scope=${scope}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await handleResponse<{ items: AssistantThreadSummary[] }>(resp);
  return data.items;
}

export async function getAssistantThread(token: string, threadId: string): Promise<AssistantThread> {
  const resp = await fetch(`${BASE}/admin/assistant/threads/${threadId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AssistantThread>(resp);
}

/** Guardar en «Mis consultas» (saved), cerrar/descartar (closed) o reactivar. */
export async function patchAssistantThread(
  token: string,
  threadId: string,
  status: 'saved' | 'closed' | 'active',
): Promise<AssistantThread> {
  const resp = await fetch(`${BASE}/admin/assistant/threads/${threadId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ status }),
  });
  return handleResponse<AssistantThread>(resp);
}

/** Aplica una acción propuesta por el composer, tras la confirmación del
 *  técnico. Corre el mismo ejecutor que el botón «Hazlo» de las sugerencias. */
export async function executeAssistantAction(
  token: string,
  actionType: string,
  params: Record<string, unknown>,
): Promise<{ resultText: string }> {
  const resp = await fetch(`${BASE}/admin/assistant/execute`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ actionType, params }),
  });
  return handleResponse<{ resultText: string }>(resp);
}

// ── Audit log ───────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  // null = acción del sistema, no de una persona del panel (la reapertura
  // automática por correo, paso 84e).
  adminUserId: string | null;
  actorName: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  beforeJson: Record<string, unknown> | null;
  afterJson: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditListResponse {
  items: AuditEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface AuditFiltersResponse {
  entities: string[];
  actions: string[];
  actors: { id: string; name: string }[];
}

export async function listAudit(
  token: string,
  params: {
    entity?: string;
    action?: string;
    adminUserId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  } = {},
): Promise<AuditListResponse> {
  const qs = new URLSearchParams();
  if (params.entity) qs.set('entity', params.entity);
  if (params.action) qs.set('action', params.action);
  if (params.adminUserId) qs.set('admin_user_id', params.adminUserId);
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const url = qs.toString() ? `${BASE}/admin/audit?${qs}` : `${BASE}/admin/audit`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AuditListResponse>(resp);
}

export async function getAuditFilters(token: string): Promise<AuditFiltersResponse> {
  const resp = await fetch(`${BASE}/admin/audit/filters`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AuditFiltersResponse>(resp);
}

// ── Toki Procesos ────────────────────────────────────────────────────

export type ProcesoStepType =
  | 'recepcion_propuestas'
  | 'revision_interna'
  | 'votacion_ciudadana'
  | 'publicacion_resultados'
  | 'sesion_presencial';
export type ProcesoActor = 'ciudadano' | 'tecnico' | 'sistema';
export type ProcesoInstanceStatus = 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
export type ProcesoStepInstanceStatus = 'pending' | 'active' | 'completed' | 'skipped';

export interface ProcesoStepInput {
  orderNum: number;
  stepType: ProcesoStepType;
  name: string;
  nameEu?: string | null;
  description?: string | null;
  descriptionEu?: string | null;
  durationDays?: number | null;
  actor: ProcesoActor;
  autoAdvance: boolean;
  config: Record<string, unknown>;
}

export interface ProcesoStepApi extends ProcesoStepInput {
  id: string;
}

export interface ProcesoTemplateApi {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  version: number;
  isActive: boolean;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  steps: ProcesoStepApi[];
}

export interface ProcesoTemplateListResponse {
  items: ProcesoTemplateApi[];
  total: number;
}

export interface ProcesoStepInstanceApi {
  id: string;
  stepOrder: number;
  stepType: ProcesoStepType;
  name: string;
  nameEu: string | null;
  description: string | null;
  descriptionEu: string | null;
  config: Record<string, unknown>;
  status: ProcesoStepInstanceStatus;
  autoAdvance: boolean;
  startedAt: string | null;
  completedAt: string | null;
  scheduledStartAt: string | null;
  scheduledEndAt: string | null;
  outputData: Record<string, unknown> | null;
}

export interface ProcesoStepOverride {
  orderNum: number;
  name?: string;
  nameEu?: string | null;
  description?: string | null;
  descriptionEu?: string | null;
  autoAdvance?: boolean;
  config?: Record<string, unknown>;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
}

// Fase totalmente definida para un proceso lanzado "desde cero" (sin plantilla).
export interface ProcesoInlineStep {
  orderNum: number;
  stepType: ProcesoStepType;
  name: string;
  nameEu?: string | null;
  description?: string | null;
  descriptionEu?: string | null;
  autoAdvance: boolean;
  config: Record<string, unknown>;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
}

export interface ProcesoInstanceApi {
  id: string;
  templateId: string | null;
  templateVersion: number | null;
  name: string;
  nameEu: string | null;
  description: string | null;
  descriptionEu: string | null;
  status: ProcesoInstanceStatus;
  currentStepOrder: number | null;
  startedAt: string | null;
  completedAt: string | null;
  imagePath: string | null;
  createdAt: string;
  stepInstances: ProcesoStepInstanceApi[];
}

export interface ProcesoInstanceSummary {
  id: string;
  templateId: string | null;
  name: string;
  nameEu: string | null;
  description: string | null;
  descriptionEu: string | null;
  status: ProcesoInstanceStatus;
  currentStepOrder: number | null;
  startedAt: string | null;
  completedAt: string | null;
  imagePath: string | null;
}

export interface ProcesoInstanceListResponse {
  items: ProcesoInstanceSummary[];
  total: number;
}

export async function listProcesoTemplates(token: string): Promise<ProcesoTemplateListResponse> {
  const resp = await fetch(`${BASE}/procesos/templates`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProcesoTemplateListResponse>(resp);
}

export async function getProcesoTemplate(id: string, token: string): Promise<ProcesoTemplateApi> {
  const resp = await fetch(`${BASE}/procesos/templates/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProcesoTemplateApi>(resp);
}

export async function createProcesoTemplate(
  data: { slug?: string; name: string; description?: string; isActive?: boolean; steps: ProcesoStepInput[] },
  token: string,
): Promise<ProcesoTemplateApi> {
  const resp = await fetch(`${BASE}/procesos/templates`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ProcesoTemplateApi>(resp);
}

export async function updateProcesoTemplate(
  id: string,
  data: { name?: string; description?: string; isActive?: boolean; steps?: ProcesoStepInput[] },
  token: string,
): Promise<ProcesoTemplateApi> {
  const resp = await fetch(`${BASE}/procesos/templates/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ProcesoTemplateApi>(resp);
}

export async function deleteProcesoTemplate(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/procesos/templates/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function listProcesoInstances(token: string): Promise<ProcesoInstanceListResponse> {
  const resp = await fetch(`${BASE}/procesos/instances`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProcesoInstanceListResponse>(resp);
}

export async function getProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function launchProcesoInstance(
  data: {
    // Una de las dos fuentes: plantilla (+ overrides) o pasos inline (desde cero).
    templateId?: string;
    name: string;
    nameEu?: string | null;
    description?: string;
    descriptionEu?: string | null;
    startNow?: boolean;
    stepOverrides?: ProcesoStepOverride[];
    steps?: ProcesoInlineStep[];
  },
  token: string,
): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function saveProcesoInstanceAsTemplate(
  id: string,
  data: { name?: string },
  token: string,
): Promise<ProcesoTemplateApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/save-as-template`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ProcesoTemplateApi>(resp);
}

export async function startProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/start`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function updateProcesoDraft(
  id: string,
  data: { name?: string; nameEu?: string | null; description?: string | null; descriptionEu?: string | null; steps: ProcesoInlineStep[] },
  token: string,
): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/draft`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function advanceProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/advance`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function pauseProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/pause`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function resumeProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/resume`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function cancelProcesoInstance(id: string, token: string): Promise<ProcesoInstanceApi> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}/cancel`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export async function deleteProcesoInstance(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/procesos/instances/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function uploadProcesoImage(id: string, file: File, token: string): Promise<ProcesoInstanceApi> {
  const fd = new FormData();
  fd.append('file', file);
  const resp = await fetch(`${BASE}/procesos/instances/${id}/image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  return handleResponse<ProcesoInstanceApi>(resp);
}

export function procesoImageUrl(instanceId: string): string {
  return `${BASE}/procesos/instances/${instanceId}/image`;
}

export async function uploadFormCoverImage(id: string, file: File, token: string): Promise<FormDefinitionApi> {
  const fd = new FormData();
  fd.append('file', file);
  const resp = await fetch(`${BASE}/forms/${id}/cover-image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export async function deleteFormCoverImage(id: string, token: string): Promise<FormDefinitionApi> {
  const resp = await fetch(`${BASE}/forms/${id}/cover-image`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<FormDefinitionApi>(resp);
}

export function formCoverImageUrl(formId: string): string {
  return `${BASE}/forms/${formId}/cover-image`;
}

export async function reviewSubmission(
  submissionId: string,
  data: { reviewStatus: 'pendiente' | 'aprobada' | 'rechazada'; reviewNote?: string | null },
  token: string,
): Promise<FormSubmissionApi> {
  const resp = await fetch(`${BASE}/forms/submissions/${submissionId}/review`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ review_status: data.reviewStatus, review_note: data.reviewNote }),
  });
  return handleResponse<FormSubmissionApi>(resp);
}

// ── Traducción al euskera (Bloque 5.2/5.3) ──────────────────────────

export interface TranslateFieldsResult {
  suggestions: Record<string, string>;
  model: string;
  tokensIn: number;
  tokensOut: number;
}

/**
 * Traducción "al vuelo": traduce el texto que el editor tiene en el formulario
 * sin necesidad de haber guardado la entidad. `texts` es {campo: texto_es}.
 * Es lo que usan los botones "Rellenar euskera con IA" de los wizards.
 */
export async function translateTexts(
  texts: Record<string, string>,
  token: string,
): Promise<TranslateFieldsResult> {
  const resp = await fetch(`${BASE}/admin/translate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ texts, targetLocale: 'eu' }),
  });
  return handleResponse<TranslateFieldsResult>(resp);
}

// ── Consultas (propuestas) ──────────────────────────────────────────

export type ConsultaStatus =
  | 'nueva'
  | 'en_curso'
  | 'respondida'
  | 'cerrada'
  | 'descartada';

export interface ConsultaAttachment {
  id: string;
  originalFilename: string;
  contentType: string;
  fileSizeBytes: number;
  url: string;
  createdAt: string;
}

export interface AdminConsulta {
  id: string;
  number: number;
  channel: string;
  title: string;
  body: string;
  citizenEmail: string | null;
  citizenPhone: string | null;
  reporterName: string | null;
  classifiedArea: { id: string; name: string } | null;
  classificationConfidence: number | null;
  status: ConsultaStatus;
  responseText: string | null;
  responseAt: string | null;
  createdAt: string;
  userId: string | null;
  assignee: { id: string; displayName: string | null; email: string } | null;
  responder: { id: string; displayName: string | null; email: string } | null;
  adminNote: string | null;
  attachments: ConsultaAttachment[];
  /** Transiciones permitidas desde el estado actual. Vacío = estado terminal. */
  validNextStatuses: ConsultaStatus[];
}

/**
 * Descarga un adjunto autenticado: el endpoint exige Bearer, así que no
 * sirve un <a href> directo. Traemos el binario como blob y forzamos la
 * descarga con el nombre original.
 */
export async function downloadConsultaAttachment(
  attachment: ConsultaAttachment,
  token: string,
): Promise<void> {
  const resp = await fetch(attachment.url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`No se pudo descargar el adjunto (${resp.status})`);
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = attachment.originalFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export interface AdminCreateConsultaBody {
  title: string;
  body: string;
  channel: 'phone' | 'email' | 'in_person' | 'app' | 'portal' | 'whatsapp' | 'chatbot';
  reporterName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  classifiedAreaId?: string | null;
  assignedTo?: string | null;
}

export async function adminCreateConsulta(
  body: AdminCreateConsultaBody,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return handleResponse<AdminConsulta>(resp);
}

export interface AdminConsultaList {
  items: AdminConsulta[];
  total: number;
  page: number;
  limit: number;
}

export interface ConsultaTimelineEvent {
  type: 'created' | 'status_change' | 'assignment' | 'response' | 'note_update';
  createdAt: string;
  actorName: string | null;
  details: Record<string, unknown>;
}

export async function listAdminConsultas(
  token: string,
  params: { status?: string; areaId?: string; assignedTo?: string; page?: number; limit?: number } = {},
): Promise<AdminConsultaList> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.areaId) qs.set('areaId', params.areaId);
  if (params.assignedTo) qs.set('assignedTo', params.assignedTo);
  if (params.page) qs.set('page', String(params.page));
  qs.set('limit', String(params.limit ?? 50));
  const resp = await fetch(`${BASE}/consultas/admin?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AdminConsultaList>(resp);
}

/**
 * Consultas cuyo área clasificada está entre las del usuario autenticado.
 * Para técnicos/coordinadores que solo deben ver lo suyo.
 */
export async function listAssignedConsultas(
  token: string,
  params: { status?: string; page?: number; limit?: number } = {},
): Promise<AdminConsultaList> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.page) qs.set('page', String(params.page));
  qs.set('limit', String(params.limit ?? 100));
  const resp = await fetch(`${BASE}/consultas/assigned?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AdminConsultaList>(resp);
}

export async function getAdminConsulta(id: string, token: string): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AdminConsulta>(resp);
}

export async function updateConsultaStatus(
  id: string,
  status: ConsultaStatus,
  note: string | null,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/status`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ status, note }),
  });
  return handleResponse<AdminConsulta>(resp);
}

export async function assignConsulta(
  id: string,
  assignedTo: string | null,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/assign`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ assignedTo }),
  });
  return handleResponse<AdminConsulta>(resp);
}

/**
 * Override manual del área que propuso la IA. Al reasignar, el backend limpia
 * la confianza IA (desaparece el chip "IA") y desasigna al técnico anterior.
 */
export async function reassignConsultaArea(
  id: string,
  classifiedAreaId: string | null,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/area`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ classifiedAreaId }),
  });
  return handleResponse<AdminConsulta>(resp);
}

export async function respondConsulta(
  id: string,
  responseText: string,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/respond`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ responseText }),
  });
  return handleResponse<AdminConsulta>(resp);
}

export async function suggestConsultaReply(
  id: string,
  token: string,
): Promise<{ draft: string | null }> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/draft-reply`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<{ draft: string | null }>(resp);
}

export async function updateConsultaNote(
  id: string,
  adminNote: string | null,
  token: string,
): Promise<AdminConsulta> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/note`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ adminNote }),
  });
  return handleResponse<AdminConsulta>(resp);
}

export async function getConsultaTimeline(id: string, token: string): Promise<ConsultaTimelineEvent[]> {
  const resp = await fetch(`${BASE}/consultas/admin/${id}/timeline`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ConsultaTimelineEvent[]>(resp);
}

// ── Hilo de conversación (consulta_messages) ─────────────────────────

export interface ConsultaMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  channel: string;
  authorType: 'requester' | 'staff' | 'system';
  authorName: string | null;
  body: string;
  isResolutory: boolean;
  readAt: string | null;
  createdAt: string;
}

/**
 * Hilo completo de una consulta. Al leerlo como staff, el backend marca como
 * leídos los mensajes entrantes del ciudadano (apaga la campana del asistente).
 */
export async function getConsultaMessages(id: string, token: string): Promise<ConsultaMessage[]> {
  const resp = await fetch(`${BASE}/consultas/${id}/messages`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ConsultaMessage[]>(resp);
}

// ── Programas (PDF eventos) ──────────────────────────────────────────

export type ProgramStatus =
  | 'processing'
  | 'pending_review'
  | 'partially_published'
  | 'completed'
  | 'discarded'
  | 'failed';

export interface ExtractedEvent {
  title: string;
  /** ISO 8601 strings con offset Madrid; vacío si el PDF no daba fechas. */
  sessions: string[];
  durationMinutes: number | null;
  location: string | null;
  description: string | null;
  category: string | null;
  targetAge: string | null;
  /** Condiciones de acceso de cualquier tipo (edad, empadronamiento, género,
   *  colectivo, aforo). Texto libre extraído por el LLM. */
  requirements: string | null;
  price: string | null;
  language: 'castellano' | 'euskera' | 'ambos' | null;
  /** Si el LLM cree que el ciudadano necesita inscribirse (pista para futura
   *  ramificación a Toki Cursos cuando exista la agenda unificada). */
  requiresRegistration: boolean | null;
  registrationHint: string | null;
  /** Workflow per-evento (se persiste dentro de extracted_json al publicar/descartar). */
  _status?: 'published' | 'discarded';
  /** Destino al publicar: 'agenda' (content_item) o 'course' (curso borrador
   *  en Toki Cursos, cuando requiresRegistration). */
  _target?: 'agenda' | 'course';
  /** IDs de los content_items creados al publicar en agenda. Uno por sesión. */
  _content_item_ids?: string[];
  /** Curso borrador creado al ramificar un evento inscribible. */
  _course_id?: string;
  _course_slug?: string;
}

export interface ProgramUploadSummary {
  id: string;
  title: string;
  area: { id: string; name: string };
  originalFilename: string;
  fileSizeBytes: number;
  pageCount: number | null;
  status: ProgramStatus;
  extractionError: string | null;
  uploader: { id: string; displayName: string | null; email: string };
  createdAt: string;
  processedAt: string | null;
  eventCount: number | null;
}

export interface ProgramUploadDetail extends ProgramUploadSummary {
  extractedText: string | null;
  extractedJson: { events: ExtractedEvent[] } | null;
  llmRawResponse: string | null;
}

export interface ProgramUploadList {
  items: ProgramUploadSummary[];
  total: number;
  page: number;
  limit: number;
}

export async function listPrograms(
  token: string,
  params: { status?: ProgramStatus; areaId?: string; page?: number; limit?: number } = {},
): Promise<ProgramUploadList> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.areaId) qs.set('areaId', params.areaId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const url = qs.toString() ? `${BASE}/admin/programs?${qs}` : `${BASE}/admin/programs`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProgramUploadList>(resp);
}

export async function getProgram(id: string, token: string): Promise<ProgramUploadDetail> {
  const resp = await fetch(`${BASE}/admin/programs/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function uploadProgram(
  body: { title: string; areaId: string; file: File },
  token: string,
): Promise<ProgramUploadSummary> {
  const form = new FormData();
  form.append('title', body.title);
  form.append('area_id', body.areaId);
  form.append('file', body.file);
  const resp = await fetch(`${BASE}/admin/programs/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  return handleResponse<ProgramUploadSummary>(resp);
}

export async function reprocessProgram(
  id: string,
  token: string,
): Promise<ProgramUploadSummary> {
  const resp = await fetch(`${BASE}/admin/programs/${id}/reprocess`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProgramUploadSummary>(resp);
}

export async function publishProgramEvent(
  uploadId: string,
  eventIdx: number,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/events/${eventIdx}/publish`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function discardProgramEvent(
  uploadId: string,
  eventIdx: number,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/events/${eventIdx}/discard`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function setProgramEventRegistration(
  uploadId: string,
  eventIdx: number,
  requiresRegistration: boolean,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/events/${eventIdx}/set-registration`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ requires_registration: requiresRegistration }),
    },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function unpublishProgramEvent(
  uploadId: string,
  eventIdx: number,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/events/${eventIdx}/unpublish`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export interface UpdateProgramEventInput {
  title?: string;
  description?: string | null;
  location?: string | null;
  category?: string | null;
  targetAge?: string | null;
  requirements?: string | null;
  price?: string | null;
  language?: string | null;
  durationMinutes?: number | null;
  sessions?: string[];
  registrationHint?: string | null;
}

export async function updateProgramEvent(
  uploadId: string,
  eventIdx: number,
  input: UpdateProgramEventInput,
  token: string,
): Promise<ProgramUploadDetail> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.description !== undefined) body.description = input.description;
  if (input.location !== undefined) body.location = input.location;
  if (input.category !== undefined) body.category = input.category;
  if (input.targetAge !== undefined) body.target_age = input.targetAge;
  if (input.requirements !== undefined) body.requirements = input.requirements;
  if (input.price !== undefined) body.price = input.price;
  if (input.language !== undefined) body.language = input.language;
  if (input.durationMinutes !== undefined) body.duration_minutes = input.durationMinutes;
  if (input.sessions !== undefined) body.sessions = input.sessions;
  if (input.registrationHint !== undefined) body.registration_hint = input.registrationHint;
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/events/${eventIdx}`,
    {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function markProgramReviewed(
  uploadId: string,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/mark-reviewed`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function reopenProgram(
  uploadId: string,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(
    `${BASE}/admin/programs/${uploadId}/reopen`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
  );
  return handleResponse<ProgramUploadDetail>(resp);
}

export async function discardProgramUpload(
  uploadId: string,
  token: string,
): Promise<void> {
  const resp = await fetch(`${BASE}/admin/programs/${uploadId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<void>(resp);
}

export async function resetProgramPublish(
  uploadId: string,
  token: string,
): Promise<ProgramUploadDetail> {
  const resp = await fetch(`${BASE}/admin/programs/${uploadId}/reset-publish`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ProgramUploadDetail>(resp);
}

// ── Activities (unified) ────────────────────────────────────────

export type ActivityKind = 'course' | 'event';

export interface AdminActivity {
  id: string;
  kind: ActivityKind;
  sourceType: string;
  courseId: string | null;
  contentItemId: string | null;
  programUploadId: string | null;
  title: string;
  description: string | null;
  imageUrl: string | null;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  address: string | null;
  category: string | null;
  status: string;
  courseSlug: string | null;
  maxCapacity: number | null;
  enrolledCount: number | null;
  price: number | null;
  requiresRegistration: boolean;
  sessionsCount: number | null;
  editable: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface AdminActivityListResponse {
  items: AdminActivity[];
  total: number;
  page: number;
  limit: number;
}

export interface ListActivitiesParams {
  kind?: ActivityKind;
  origin?: string;
  status?: string;
  search?: string;
  programUploadId?: string;
  page?: number;
  limit?: number;
}

export async function listActivities(
  params: ListActivitiesParams,
  token: string,
): Promise<AdminActivityListResponse> {
  const qs = new URLSearchParams();
  if (params.kind) qs.set('kind', params.kind);
  if (params.origin) qs.set('origin', params.origin);
  if (params.status) qs.set('status', params.status);
  if (params.search) qs.set('search', params.search);
  if (params.programUploadId) qs.set('program_upload_id', params.programUploadId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const resp = await fetch(`${BASE}/admin/activities?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AdminActivityListResponse>(resp);
}

// ── Content items (editar evento publicado desde PDF) ──────────

export interface ContentItem {
  id: string;
  sourceType: string;
  externalId: string;
  locale: string;
  status: string;
  title: string;
  titleEu: string | null;
  slug: string | null;
  summary: string | null;
  summaryEu: string | null;
  body: string | null;
  bodyEu: string | null;
  imageUrl: string | null;
  url: string | null;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  category: string | null;
  eventLanguage: string | null;
  startsAt: string | null;
  endsAt: string | null;
  publishedAt: string | null;
  extra: Record<string, unknown> | null;
}

export async function getContentItem(itemId: string, token: string): Promise<ContentItem> {
  const resp = await fetch(`${BASE}/content/items/${itemId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<ContentItem>(resp);
}

export interface UpdateContentItemInput {
  title?: string;
  titleEu?: string | null;
  summary?: string | null;
  summaryEu?: string | null;
  body?: string | null;
  bodyEu?: string | null;
  imageUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  address?: string | null;
  category?: string | null;
  /** Idioma del evento: 'es' | 'eu' | 'bilingue'. */
  language?: string | null;
  /**
   * Lista de ISO 8601 con offset (Europe/Madrid). Si se envía, el backend
   * recalcula starts_at/ends_at desde la primera y última sesión (a menos
   * que el caller también las fije explícitamente).
   */
  sessions?: string[];
}

export async function updateContentItem(
  itemId: string,
  input: UpdateContentItemInput,
  token: string,
): Promise<ContentItem> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.titleEu !== undefined) body.title_eu = input.titleEu;
  if (input.summary !== undefined) body.summary = input.summary;
  if (input.summaryEu !== undefined) body.summary_eu = input.summaryEu;
  if (input.body !== undefined) body.body = input.body;
  if (input.bodyEu !== undefined) body.body_eu = input.bodyEu;
  if (input.imageUrl !== undefined) body.image_url = input.imageUrl;
  if (input.startsAt !== undefined) body.starts_at = input.startsAt;
  if (input.endsAt !== undefined) body.ends_at = input.endsAt;
  if (input.address !== undefined) body.address = input.address;
  if (input.category !== undefined) body.category = input.category;
  if (input.language !== undefined) body.language = input.language;
  if (input.sessions !== undefined) body.sessions = input.sessions;
  const resp = await fetch(`${BASE}/content/items/${itemId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse<ContentItem>(resp);
}

export async function deleteContentItem(itemId: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/content/items/${itemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok && resp.status !== 204) {
    return handleResponse<void>(resp);
  }
}

export interface CreateContentItemInput {
  title: string;
  titleEu?: string | null;
  summary?: string | null;
  summaryEu?: string | null;
  body?: string | null;
  bodyEu?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  address?: string | null;
  category?: string | null;
  language?: string | null;
  imageUrl?: string | null;
}

export async function createContentItem(
  input: CreateContentItemInput,
  token: string,
): Promise<ContentItem> {
  const body: Record<string, unknown> = { title: input.title };
  if (input.titleEu !== undefined) body.title_eu = input.titleEu;
  if (input.summary !== undefined) body.summary = input.summary;
  if (input.summaryEu !== undefined) body.summary_eu = input.summaryEu;
  if (input.body !== undefined) body.body = input.body;
  if (input.bodyEu !== undefined) body.body_eu = input.bodyEu;
  if (input.startsAt !== undefined) body.starts_at = input.startsAt;
  if (input.endsAt !== undefined) body.ends_at = input.endsAt;
  if (input.address !== undefined) body.address = input.address;
  if (input.category !== undefined) body.category = input.category;
  if (input.language !== undefined) body.language = input.language;
  if (input.imageUrl !== undefined) body.image_url = input.imageUrl;
  const resp = await fetch(`${BASE}/content/items`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse<ContentItem>(resp);
}

export async function setContentItemStatus(
  itemId: string,
  status: 'draft' | 'published',
  token: string,
): Promise<ContentItem> {
  const resp = await fetch(`${BASE}/content/items/${itemId}/status`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  return handleResponse<ContentItem>(resp);
}

// ── Chatbot config ───────────────────────────────────────────────────

export interface ChatbotConfig {
  enabled: boolean;
  disabledMessage: string | null;
  extraInstructions: string | null;
  basePrompt: string;
}

export interface ChatbotConfigUpdate {
  enabled: boolean;
  disabledMessage: string | null;
  extraInstructions: string | null;
}

export async function getChatbotConfig(token: string): Promise<ChatbotConfig> {
  const resp = await fetch(`${BASE}/admin/chatbot/config`, { headers: authHeaders(token) });
  return handleResponse<ChatbotConfig>(resp);
}

export async function updateChatbotConfig(
  data: ChatbotConfigUpdate,
  token: string,
): Promise<ChatbotConfig> {
  const resp = await fetch(`${BASE}/admin/chatbot/config`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<ChatbotConfig>(resp);
}

// ── Información municipal / FAQ (corpus RAG del chatbot) ──────────────

export interface FaqItem {
  id: string;
  question: string;
  questionEu: string | null;
  answer: string | null;
  answerEu: string | null;
  category: string | null;
  status: string; // 'draft' | 'published'
  translationMissing: { question?: boolean; answer?: boolean };
  updatedAt: string | null;
}

export interface FaqWrite {
  question?: string;
  questionEu?: string | null;
  answer?: string | null;
  answerEu?: string | null;
  category?: string | null;
  status?: string;
}

export async function listFaqItems(token: string, locale = 'es'): Promise<FaqItem[]> {
  const resp = await fetch(`${BASE}/content/info?locale=${locale}`, { headers: authHeaders(token) });
  return handleResponse<FaqItem[]>(resp);
}

export async function createFaqItem(data: FaqWrite, token: string): Promise<FaqItem> {
  const resp = await fetch(`${BASE}/content/info`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<FaqItem>(resp);
}

export async function updateFaqItem(id: string, data: FaqWrite, token: string): Promise<FaqItem> {
  const resp = await fetch(`${BASE}/content/info/${id}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<FaqItem>(resp);
}

export async function deleteFaqItem(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/content/info/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse<void>(resp);
}

export async function reembedFaqCorpus(token: string): Promise<{ embedded: number }> {
  const resp = await fetch(`${BASE}/content/info/reembed`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<{ embedded: number }>(resp);
}

// ── Documentos PDF del corpus RAG (ordenanzas, reglamentos…) ─────────

export interface RagDocument {
  id: string;
  title: string;
  origin: string;
  sourceUrl: string | null;
  originalFilename: string | null;
  locale: string;
  status: string; // 'processing' | 'completed' | 'failed'
  pageCount: number | null;
  chunkCount: number | null;
  error: string | null;
  createdAt: string;
  processedAt: string | null;
}

export async function listRagDocuments(token: string): Promise<RagDocument[]> {
  const resp = await fetch(`${BASE}/admin/documents`, { headers: authHeaders(token) });
  return handleResponse<RagDocument[]>(resp);
}

export async function uploadRagDocument(
  body: { title: string; locale: string; sourceUrl?: string; file: File },
  token: string,
): Promise<RagDocument> {
  const form = new FormData();
  form.append('title', body.title);
  form.append('locale', body.locale);
  if (body.sourceUrl) form.append('source_url', body.sourceUrl);
  form.append('file', body.file);
  const resp = await fetch(`${BASE}/admin/documents/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  return handleResponse<RagDocument>(resp);
}

export async function deleteRagDocument(id: string, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/admin/documents/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse<void>(resp);
}

export async function crawlOrdinances(token: string): Promise<{ status: string; message: string }> {
  const resp = await fetch(`${BASE}/admin/documents/crawl-ordinances`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse<{ status: string; message: string }>(resp);
}

// ── Parámetros operativos (app_settings) ─────────────────────────────

export interface SettingItem {
  key: string;
  label: string;
  help: string;
  group: string;
  type: string; // 'int' | 'float' | 'str' | 'url' | 'email'
  value: string;
  default: string;
  min: number | null;
  max: number | null;
}

export interface SettingsGroup {
  key: string;
  label: string;
  items: SettingItem[];
}

export interface SettingsResponse {
  groups: SettingsGroup[];
}

export async function getSettings(token: string): Promise<SettingsResponse> {
  const resp = await fetch(`${BASE}/admin/settings`, { headers: authHeaders(token) });
  return handleResponse<SettingsResponse>(resp);
}

export async function updateSettings(
  values: Record<string, string>,
  token: string,
): Promise<SettingsResponse> {
  const resp = await fetch(`${BASE}/admin/settings`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ values }),
  });
  return handleResponse<SettingsResponse>(resp);
}


// ── Informes (paso 88) ───────────────────────────────────────────────
//
// La pantalla pinta cualquier tabla por el `tipo` de sus columnas: un informe
// nuevo del catálogo no necesita código aquí. Los minutos llegan como número
// (horas y duraciones) y las fechas en ISO; se formatean en la zona que manda
// el servidor (`zona`), no en la del navegador. El PDF lo genera el servidor a
// partir del MISMO contenido (`app/informes/router.py::_payload`).

export type InformeTipo = 'texto' | 'entero' | 'horas' | 'duracion' | 'porcentaje' | 'fecha' | 'fechahora' | 'sino';

export interface InformeCatalogoItem {
  id: string;
  grupo: string;
  titulo: string;
  descripcion: string;
  filtros: string[];
  versionCliente: boolean;
}

export interface InformeOpcion { id: string; nombre: string; activo?: boolean }

export interface InformesCatalogo {
  zona: string;
  informes: InformeCatalogoItem[];
  clientes: InformeOpcion[];
  tecnicos: InformeOpcion[];
  equipos: InformeOpcion[];
  canales: InformeOpcion[];
}

export interface InformeColumna { clave: string; rotulo: string; tipo: InformeTipo }
export type InformeFila = Record<string, string | number | boolean | null>;
export interface InformeTabla {
  titulo: string;
  columnas: InformeColumna[];
  filas: InformeFila[];
  total: InformeFila | null;
  /** La columna que dibuja la barra de proporción bajo el nombre de la fila. */
  barra: string | null;
}

export interface InformeKpi {
  clave: string;
  rotulo: string;
  valor: number | null;
  tipo: InformeTipo;
  nota: string | null;
  /** Hacia dónde es mejor que vaya al comparar. Null = ni bien ni mal. */
  mejor: 'sube' | 'baja' | null;
  /** El mismo valor en el periodo anterior (null si no se compara). */
  anterior: number | null;
}

export interface InformeResultado {
  informe: { id: string; titulo: string; grupo: string };
  titulo: string;
  subtitulo: string;
  version: 'interna' | 'cliente';
  desde: string;
  hasta: string;
  comparar: boolean;
  periodoAnterior: { desde: string; hasta: string } | null;
  cliente: { id: string; nombre: string } | null;
  generadoEl: string;
  /** K62 (vía a): horas de este periodo tocadas desde la última descarga o
   *  impresión de ESTE informe con los mismos filtros. Null = nada. */
  cambiosDesdeEmision: {
    emitidoEl: string;
    emitidoPor: string | null;
    apuntes: number;
    deltaMinutos: number;
    deltaFacturables: number;
  } | null;
  zona: string;
  kpis: InformeKpi[];
  grafico: {
    titulo: string;
    tipo: InformeTipo;
    tramos: { rotulo: string; valor: number | null; anterior: number | null }[];
  };
  resumen: InformeTabla;
  detalle: InformeTabla;
  leyenda: string;
}

export interface InformeFiltros {
  desde: string;
  hasta: string;
  /** Id de cliente, 'sin' (tickets sin cliente) o vacío (todos). */
  cliente?: string;
  tecnico?: string;
  equipo?: string;
  canal?: string;
  version?: 'interna' | 'cliente';
  comparar?: boolean;
}

function informeQuery(f: InformeFiltros): string {
  const q = new URLSearchParams({ desde: f.desde, hasta: f.hasta });
  if (f.cliente) q.set('cliente', f.cliente);
  if (f.tecnico) q.set('tecnico', f.tecnico);
  if (f.equipo) q.set('equipo', f.equipo);
  if (f.canal) q.set('canal', f.canal);
  if (f.version) q.set('version', f.version);
  if (f.comparar !== undefined) q.set('comparar', String(f.comparar));
  return q.toString();
}

export async function getInformesCatalogo(token: string): Promise<InformesCatalogo> {
  const resp = await fetch(`${BASE}/informes`, { headers: authHeaders(token) });
  return handleResponse<InformesCatalogo>(resp);
}

/** ⚠️ Abrir un informe deja rastro en la auditoría (lo impreso no pasa por el servidor). */
export async function getInforme(id: string, f: InformeFiltros, token: string): Promise<InformeResultado> {
  const resp = await fetch(`${BASE}/informes/${id}?${informeQuery(f)}`, { headers: authHeaders(token) });
  return handleResponse<InformeResultado>(resp);
}

/** El aviso de impresión (Ctrl+P): solo audita. Si falla, se imprime igual. */
export async function avisarInformeImpreso(id: string, f: InformeFiltros, token: string): Promise<void> {
  const resp = await fetch(`${BASE}/informes/${id}/impreso`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(f),
  });
  return handleResponse<void>(resp);
}

/** Descarga por fetch + blob, como los adjuntos: el token no viaja en la URL. */
export async function downloadInforme(
  id: string,
  f: InformeFiltros,
  format: 'xlsx' | 'csv' | 'pdf',
  hoja: 'resumen' | 'detalle',
  token: string,
): Promise<void> {
  const resp = await fetch(`${BASE}/informes/${id}/export?format=${format}&hoja=${hoja}&${informeQuery(f)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) await handleResponse(resp);
  const cd = resp.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const filename = m?.[1] ? decodeURIComponent(m[1]) : `informe-${id}.${format}`;
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Asistente del portal (pasos 92a y 92b) ────────────────────────────
// ⚠️ No es el «Asistente» del raíl (`getAssistantCount` y compañía, el de
// staff). Este es el chatbot del PORTAL del cliente: responde con preguntas
// frecuentes (texto LITERAL de su versión vigente) y, si no hay, con los
// documentos PDF visibles para clientes, citando documento y página.

/** La cita de una respuesta redactada con un documento (92b). */
export interface AsistenteFuente {
  documentoId: string;
  nombre: string;
  pagina: number;
}

export interface AsistenteRespuesta {
  id: string;
  resultado: 'faq' | 'documento' | 'sin_coincidencia' | 'sin_servicio';
  respuesta: string;
  fuente: AsistenteFuente | null;
  ofrecerTicket: boolean;
}

export async function getAsistenteEstado(token: string): Promise<{ activo: boolean }> {
  const resp = await fetch(`${BASE}/portal/asistente/estado`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<{ activo: boolean }>(resp);
}

export async function preguntarAsistente(token: string, texto: string): Promise<AsistenteRespuesta> {
  const resp = await fetch(`${BASE}/portal/asistente/preguntas`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ texto }),
  });
  return handleResponse<AsistenteRespuesta>(resp);
}

/** El PDF de una cita, como blob (D1 del 92b). Solo existe si el documento
 *  es visible para clientes; si no, 404. Viene como binario: el tipo se le
 *  pone aquí para que el navegador lo abra en su visor. */
export async function descargarDocumentoAsistente(token: string, documentoId: string): Promise<Blob> {
  const resp = await fetch(`${BASE}/portal/asistente/documentos/${documentoId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) await handleResponse(resp);
  return new Blob([await resp.blob()], { type: 'application/pdf' });
}

export async function valorarAsistente(
  token: string, id: string, util: boolean,
): Promise<{ id: string; util: boolean; ofrecerTicket: boolean }> {
  const resp = await fetch(`${BASE}/portal/asistente/preguntas/${id}/valoracion`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ util }),
  });
  return handleResponse(resp);
}

export interface PortalFaqVersion {
  n: number;
  respuesta: string;
  nota: string | null;
  autor: string;
  createdAt: string;
}

export interface PortalFaq {
  id: string;
  pregunta: string;
  categoryId: string | null;
  categoria: string | null;
  activa: boolean;
  versionActual: number;
  variantes: string[];
  /** De la más nueva a la más vieja. */
  versiones: PortalFaqVersion[];
  /** Frases sin vector: no encajan con nada hasta reindexar. */
  sinIndexar: number;
  updatedAt: string;
}

export interface AsistenteConfig {
  activo: boolean;
  embeddingsConfigurados: boolean;
  /** Sin modelo de texto los documentos no se usan (92b). */
  textoConfigurado: boolean;
}

export type AsistentePasoResultado =
  | 'respondida' | 'sin_coincidencia' | 'no_hizo_falta' | 'sin_servicio' | 'ofrecido';

export interface AsistenteProbar {
  resultado: AsistenteRespuesta['resultado'];
  respuesta: string;
  fuente: AsistenteFuente | null;
  ofrecerTicket: boolean;
  faqId: string | null;
  preguntaFaq: string | null;
  version: number | null;
  puntuacion: number | null;
  documentoPuntuacion: number | null;
  umbral: number;
  umbralDoc: number;
  motivo: string;
  /** Los tres escalones, en orden: faq · documentos · ticket. */
  pasos: { escalon: 'faq' | 'documentos' | 'ticket'; resultado: AsistentePasoResultado; detalle: string }[];
  candidatas: { faqId: string; pregunta: string; frase: string; puntuacion: number }[];
  trozos: { documentoId: string; nombre: string; pagina: number; puntuacion: number; extracto: string }[];
  sinIndexar: number;
  trozosSinIndexar: number;
}

const AP = `${BASE}/admin/asistente-portal`;

export async function getAsistenteConfig(token: string): Promise<AsistenteConfig> {
  return handleResponse(await fetch(`${AP}/config`, { headers: { Authorization: `Bearer ${token}` } }));
}

export async function setAsistenteActivo(token: string, activo: boolean): Promise<AsistenteConfig> {
  return handleResponse(await fetch(`${AP}/config`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ activo }),
  }));
}

export async function listPortalFaqs(token: string): Promise<{ items: PortalFaq[]; puedeEditar: boolean }> {
  return handleResponse(await fetch(`${AP}/faqs`, { headers: { Authorization: `Bearer ${token}` } }));
}

export async function createPortalFaq(
  token: string,
  body: { pregunta: string; categoryId: string | null; variantes: string[]; respuesta: string },
): Promise<PortalFaq> {
  return handleResponse(await fetch(`${AP}/faqs`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(body),
  }));
}

export async function updatePortalFaq(
  token: string, id: string,
  body: { pregunta: string; categoryId: string | null; variantes: string[] },
): Promise<PortalFaq> {
  return handleResponse(await fetch(`${AP}/faqs/${id}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(body),
  }));
}

export async function savePortalFaqVersion(
  token: string, id: string, respuesta: string, nota: string,
): Promise<PortalFaq> {
  return handleResponse(await fetch(`${AP}/faqs/${id}/versiones`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ respuesta, nota: nota || null }),
  }));
}

export async function restorePortalFaqVersion(token: string, id: string, n: number): Promise<PortalFaq> {
  return handleResponse(await fetch(`${AP}/faqs/${id}/versiones/${n}/restaurar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  }));
}

export async function setPortalFaqActiva(token: string, id: string, activa: boolean): Promise<PortalFaq> {
  return handleResponse(await fetch(`${AP}/faqs/${id}/activa`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ activa }),
  }));
}

export async function reindexarPortalFaqs(token: string): Promise<{ indexadas: number; pendientes: number }> {
  return handleResponse(await fetch(`${AP}/reindexar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  }));
}

export async function probarAsistente(token: string, texto: string): Promise<AsistenteProbar> {
  return handleResponse(await fetch(`${AP}/probar`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ texto }),
  }));
}

// Documentos (92b)

export type DocumentoEstado = 'procesando' | 'listo' | 'sin_texto' | 'error';
export type DocumentoVisibilidad = 'clientes' | 'agentes';

export interface PortalDocumento {
  id: string;
  nombre: string;
  sizeBytes: number;
  paginas: number | null;
  paginasSinTexto: number | null;
  visibilidad: DocumentoVisibilidad;
  activo: boolean;
  estado: DocumentoEstado;
  error: string | null;
  trozos: number;
  /** Trozos sin vector: no encajan con nada hasta reindexar. */
  sinIndexar: number;
  autor: string;
  createdAt: string;
  procesadoAt: string | null;
}

export async function listPortalDocumentos(
  token: string,
): Promise<{ items: PortalDocumento[]; puedeEditar: boolean; maxMb: number }> {
  return handleResponse(await fetch(`${AP}/documentos`, { headers: { Authorization: `Bearer ${token}` } }));
}

export async function subirPortalDocumento(token: string, fichero: File): Promise<PortalDocumento> {
  const fd = new FormData();
  fd.append('fichero', fichero);
  return handleResponse(await fetch(`${AP}/documentos`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
  }));
}

export async function editarPortalDocumento(
  token: string, id: string, body: { visibilidad?: DocumentoVisibilidad; activo?: boolean },
): Promise<PortalDocumento> {
  return handleResponse(await fetch(`${AP}/documentos/${id}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(body),
  }));
}

export async function reprocesarPortalDocumento(token: string, id: string): Promise<PortalDocumento> {
  return handleResponse(await fetch(`${AP}/documentos/${id}/procesar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  }));
}

export async function eliminarPortalDocumento(token: string, id: string): Promise<void> {
  const resp = await fetch(`${AP}/documentos/${id}`, {
    method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) await handleResponse(resp);
}

/** El PDF para staff (también los «Solo agentes»), como blob con su tipo. */
export async function descargarPortalDocumento(token: string, id: string): Promise<Blob> {
  const resp = await fetch(`${AP}/documentos/${id}/fichero`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) await handleResponse(resp);
  return new Blob([await resp.blob()], { type: 'application/pdf' });
}

// ── Parámetros (paso 101) ─────────────────────────────────────────────

export interface ParamItem {
  key: string;
  label: string;
  help: string;
  type: 'int' | 'float' | 'bool';
  /** El valor que vale ahora, en texto. */
  value: string;
  /** El de por defecto (el del código), en texto. */
  default: string;
  min: number | null;
  max: number | null;
  unit: string;
}

export interface ParamsResponse {
  families: { key: string; label: string }[];
  groups: { key: string; label: string; family: string; items: ParamItem[] }[];
}

export async function getParametros(token: string): Promise<ParamsResponse> {
  return handleResponse(await fetch(`${BASE}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }));
}

export async function saveParametros(token: string, values: Record<string, string>): Promise<ParamsResponse> {
  return handleResponse(await fetch(`${BASE}/admin/settings`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ values }),
  }));
}

// ── «¿Qué harías aquí?» (paso 51b) ────────────────────────────────────

export interface SiguientePaso {
  id: string;
  recomendacion: string;
  acciones: AssistantActionCard[];
}

export async function pedirSiguientePaso(token: string, incidenciaId: string): Promise<SiguientePaso> {
  return handleResponse(await fetch(`${BASE}/admin/assistant/siguiente-paso`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ incidenciaId }),
  }));
}

/** El KPI: qué hizo el técnico con la propuesta. Best-effort: si falla, no se
 *  le dice nada a nadie. */
export async function resultadoSiguientePaso(
  token: string, id: string, resultado: 'aplicada' | 'descartada', accion?: string,
): Promise<void> {
  await fetch(`${BASE}/admin/assistant/siguiente-paso/${id}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ resultado, accion }),
  }).catch(() => undefined);
}
